import React, { Fragment, useState, useEffect, useContext, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import {
  XMarkIcon,
  XCircleIcon,
  ChevronDownIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import { captureException } from '@sentry/nextjs'
import { classNames } from 'app/utils/classes'
import { useTranslation } from 'next-i18next'
import { Cart, Price, Product, Ticket } from 'app/types/authorization'
import { ItemPicture } from './Image'
import { StandardAPIRequest } from 'app/types/api'
import { clientGetIdToken } from 'app/utils/cognito'
import { CompanyProfile } from 'app/types/organization'
import { capitalize, truncateForCart } from 'app/utils/text'
import { AuthContext } from 'app/utils/context'
import { Item, Size } from 'app/types/items'
import { TFunction } from 'next-i18next'
import Currency from 'app/types/currency'
import { PublicPool } from 'app/types/pools'
import { formatTimeLeft } from 'app/utils/time'

// Types
interface VenueCartProps {
  eventId?: string
  seasonTicketId?: string
  organisationId?: string
  currentStep: number
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
  refetchCart: () => void
  refetchTickets: () => Promise<void>
  setShowSuccessMessage: React.Dispatch<React.SetStateAction<string>>
  setShowFailureMessage: React.Dispatch<React.SetStateAction<string>>
  cart: Cart | undefined
  isRegister: boolean
  companyProfile: CompanyProfile | null
  shouldHaveItems: boolean
  shouldHaveKiosk: boolean
  shouldHaveRestaurant: boolean
  ticketsInCart: string[]
  showOrder: boolean
  setShowOrder: React.Dispatch<React.SetStateAction<boolean>>
  setShowCampaignCode: React.Dispatch<React.SetStateAction<boolean>>
  showCampaignCode: boolean
  hideProceedButton: boolean
}

// Reusable components
const DeleteButton: React.FC<{ onClick: () => void; color?: string }> = ({
  onClick,
  color,
}) => (
  <XCircleIcon
    onClick={onClick}
    style={{
      stroke: color !== '#FFFFFF' && color !== '#FFFEFE' ? color : '#000000',
    }}
    className="h-8 w-8 cursor-pointer fill-white opacity-80"
  />
)

const PriceDisplay: React.FC<{
  value: number | undefined
  currency: string
  color?: string
}> = ({ value, currency, color }) => (
  <div className="flex flex-row items-center gap-1">
    <p style={{ color }} className="text-base font-extrabold">
      {(value ?? 0).toFixed(2)}
    </p>
    <p style={{ color }} className="mt-1 text-xs">
      {currency}
    </p>
  </div>
)

interface CampaignListProps {
  campaigns: Cart['campaigns']
  deleteCampaignFromCart: (code: string) => void
  companyProfile: CompanyProfile | null
  t: TFunction
}

// Campaign List Component
const CampaignList: React.FC<CampaignListProps> = ({
  campaigns,
  deleteCampaignFromCart,
  companyProfile,
  t,
}) => (
  <ul role="list" className="flex flex-col">
    {campaigns?.map((campaign) => (
      <li
        key={`${campaign.pk}-${campaign.sk}`}
        className="flex w-full flex-col pr-4"
      >
        <div className="mt-4 flex flex-row">
          <div className="w-[90%] min-w-[100px] drop-shadow-md sm:min-w-[320px]">
            <div id="" className="flex flex-row">
              <div className="flex w-1/2 flex-row justify-between rounded-tl border-b-2 border-r-2 border-gray-700 bg-white p-1 px-2 text-gray-600">
                <p className="ml-1 text-sm">{t('campaign')}</p>
                <p className="mt-2 text-base font-extrabold text-gray-600">
                  {campaign.name ?? '-'}
                </p>
              </div>
              <div className="flex w-1/2 flex-row justify-between border-b-2 border-r-2 border-gray-700 bg-white p-1 px-2 text-gray-600">
                <p className="text-sm">{t('type')}</p>
                <p className="mt-2 text-base font-extrabold text-gray-600">
                  {campaign.type_of_campaign === 'Fixed' && t('fixed-discount')}
                  {campaign.type_of_campaign === 'Percentage' &&
                    t('percentage-discount')}
                  {campaign.type_of_campaign === 'TwoForOne' &&
                    t('two-for-one')}
                </p>
              </div>
            </div>
          </div>
          <div className="ml-4 self-center">
            <DeleteButton
              onClick={() =>
                campaign.code !== null && deleteCampaignFromCart(campaign.code)
              }
              color={companyProfile?.primary_main}
            />
          </div>
        </div>
      </li>
    ))}
  </ul>
)

interface TicketListProps {
  tickets: Cart['items']
  deleteTicketFromCart: (pk: string, sk: string) => void
  updatePrice: (ticket: Ticket, price: Price, index: number) => void
  companyProfile: CompanyProfile | null
  openDropdown: { [key: number]: boolean }
  setOpenDropdown: React.Dispatch<
    React.SetStateAction<{ [key: number]: boolean }>
  >
  t: TFunction
}
// Ticket List Component
const TicketList: React.FC<TicketListProps> = ({
  tickets,
  deleteTicketFromCart,
  updatePrice,
  companyProfile,
  openDropdown,
  setOpenDropdown,
  t,
}) => (
  <ul role="list" className="flex flex-col">
    {tickets?.map((product, index) => {
      if (product.Ticket !== undefined) {
        return (
          <li
            key={`${index}-${product.Ticket.pk}-${product.Ticket.sk}`}
            className="flex w-full flex-col pr-4"
          >
            <div className="mt-4 flex flex-row">
              <div className="w-[90%] min-w-[100px] drop-shadow-md sm:min-w-[320px]">
                <div id="" className="flex flex-row">
                  <div className="flex w-1/2 flex-row justify-between rounded-tl border-b-2 border-r-2 border-gray-700 bg-white p-1 px-2 text-gray-600">
                    <p className="ml-1 text-sm">{t('section')}</p>
                    <p className="mt-2 text-base font-extrabold text-gray-600">
                      {product.Ticket.section_name ?? '-'}
                    </p>
                  </div>
                  <div className="flex w-1/4 flex-row justify-between border-b-2 border-r-2 border-gray-700 bg-white p-1 px-2 text-gray-600">
                    <p className="text-sm">{capitalize(t('row'))}</p>
                    <p className="mt-2 text-base font-extrabold text-gray-600">
                      {product.Ticket.row ?? '-'}
                    </p>
                  </div>
                  <div className="flex w-1/4 flex-row justify-between rounded-tr border-b-2 border-gray-700 bg-white p-1 px-2 text-gray-600">
                    <p className="text-sm">{capitalize(t('seat'))}</p>
                    <p className="mt-2 text-base font-extrabold text-gray-600">
                      {product.Ticket.seat ?? '-'}
                    </p>
                  </div>
                </div>
                <div className="flex w-full flex-row">
                  <div
                    style={{
                      backgroundColor: companyProfile?.primary_main,
                    }}
                    className={classNames(
                      companyProfile?.primary_inverted
                        ? 'text-gray-600'
                        : 'text-white',
                      'flex w-1/2 cursor-pointer flex-row items-center justify-between rounded-bl border-r-2 border-gray-700 p-1 px-2'
                    )}
                    onClick={() =>
                      setOpenDropdown((prev) => ({
                        ...prev,
                        [index]: !prev[index],
                      }))
                    }
                  >
                    <p className="ml-1 text-sm">
                      {capitalize(product.Ticket.selected_price?.name ?? '')}
                      <ChevronDownIcon
                        className={classNames(
                          companyProfile?.primary_inverted
                            ? 'text-gray-600'
                            : 'text-white',
                          'inline-block h-4 w-4'
                        )}
                      />
                    </p>
                  </div>
                  <div
                    style={{
                      backgroundColor: companyProfile?.primary_main,
                    }}
                    className={classNames(
                      companyProfile?.primary_inverted
                        ? 'text-gray-600'
                        : 'text-white',
                      'flex w-1/2 flex-row items-center justify-between rounded-br p-1 px-2'
                    )}
                  >
                    <PriceDisplay
                      value={product.Ticket.selected_price?.value}
                      currency={product.Ticket?.currency ?? ''}
                      color={
                        companyProfile?.primary_inverted ? '#4B5563' : '#FFFFFF'
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="ml-4 self-center">
                <DeleteButton
                  onClick={() => {
                    if (product.Ticket) {
                      deleteTicketFromCart(product.Ticket.pk, product.Ticket.sk)
                    }
                  }}
                  color={companyProfile?.primary_main}
                />
              </div>
            </div>
            <div
              id="dropdown"
              className={classNames(
                'z-40 mt-2 w-44 divide-y rounded-lg bg-white shadow',
                openDropdown[index] ? 'block' : 'hidden'
              )}
              style={{
                backgroundColor: companyProfile?.primary_main,
              }}
            >
              <ul
                className="py-2 text-sm text-gray-700"
                aria-labelledby="dropdownDefaultButton"
              >
                {product.Ticket.price?.map((price, index) => (
                  <li key={`${index}-${price.name}`}>
                    <div
                      className={classNames(
                        companyProfile?.primary_inverted
                          ? 'text-gray-600'
                          : 'text-white',
                        'block cursor-pointer px-4 py-2'
                      )}
                      onClick={() => {
                        if (product.Ticket) {
                          updatePrice(product.Ticket, price, index)
                        }
                      }}
                    >
                      {capitalize(price.name ?? '')} - {price.value}{' '}
                      {product.Ticket?.currency}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </li>
        )
      }
    })}
  </ul>
)

interface ItemListProps {
  items: Cart['items']
  deleteTicketFromCart: (pk: string, sk: string) => void
  updateSize: (item: Item, size: Size, index: number) => void
  companyProfile: CompanyProfile | null
  openDropdown: { [key: number]: boolean }
  setOpenDropdown: React.Dispatch<
    React.SetStateAction<{ [key: number]: boolean }>
  >
  t: TFunction
}
// Item List Component
const ItemList: React.FC<ItemListProps> = ({
  items,
  deleteTicketFromCart,
  updateSize,
  companyProfile,
  openDropdown,
  setOpenDropdown,
  t,
}) => {
  const groupedItems = items.reduce((acc, product) => {
    if (product.Item) {
      const key = `${product.Item.pk}-${product.Item.sk}-${product.Item.selected_size?.name}`
      if (!acc[key]) {
        acc[key] = { ...product, quantity: 1 }
      } else {
        acc[key].quantity += 1
      }
    }
    return acc
  }, {} as { [key: string]: Product & { quantity: number } })

  return (
    <>
      {Object.values(groupedItems).map((product, index) => {
        if (product.Item !== undefined) {
          const isFirstItem = index === 0
          return (
            <React.Fragment
              key={`${index}-${product.Item.pk}-${product.Item.sk}`}
            >
              {isFirstItem && (
                <h2
                  id="cart-heading"
                  className="-mb-2 mt-5 text-lg font-semibold text-gray-600"
                  style={{
                    fontFamily: companyProfile?.font?.name,
                  }}
                >
                  {t('merch')}
                </h2>
              )}
              <li className="mt-4 flex w-full flex-col pr-4">
                <div className="flex flex-row rounded-l">
                  <div className="w-[90%] min-w-[320px]">
                    <div className="flex flex-row rounded-l drop-shadow-md">
                      <div className="w-1/5 rounded-l">
                        <div
                          id="bild"
                          style={{
                            borderRightWidth: 2,
                            borderRightColor: companyProfile?.primary_main,
                          }}
                          className="flex h-full w-full items-center rounded-l bg-white"
                        >
                          <ItemPicture
                            imageLink={product.Item.image_link ?? undefined}
                          />
                        </div>
                      </div>
                      <div className="flex w-4/5 flex-col">
                        <div
                          style={{
                            borderBottomColor: companyProfile?.primary_inverted
                              ? '#000000'
                              : companyProfile?.primary_main,
                            color: companyProfile?.primary_inverted
                              ? '#000000'
                              : companyProfile?.primary_main,
                            borderBottomWidth: 2,
                          }}
                          className="flex h-1/2 items-center rounded-tr bg-white py-4 pl-3 text-base font-medium leading-tight"
                        >
                          <p className="">
                            {product.Item.name} (x{product.quantity})
                          </p>
                        </div>
                        <div className="flex h-1/2 w-full flex-row">
                          <div
                            className="flex w-3/5 cursor-pointer flex-row items-center justify-between rounded-bl bg-white p-1 px-2 text-white"
                            onClick={() =>
                              setOpenDropdown((prev) => ({
                                ...prev,
                                [index]: !prev[index],
                              }))
                            }
                          >
                            <p className="ml-1 text-sm text-black">
                              {capitalize(
                                product.Item.selected_size?.name ?? ''
                              )}
                              <ChevronDownIcon className="inline-block h-4 w-4 text-black" />
                            </p>
                          </div>
                          <div
                            style={{
                              borderLeftColor: companyProfile?.primary_main,
                              borderLeftWidth: 2,
                            }}
                            className="flex w-2/5 flex-row items-center justify-center rounded-br bg-white"
                          >
                            <PriceDisplay
                              value={
                                (product.Item.selected_price?.value ?? 0) *
                                product.quantity
                              }
                              currency={product.Item.currency}
                              color={
                                companyProfile?.primary_inverted
                                  ? '#4B5563'
                                  : companyProfile?.primary_main
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 self-center">
                    <DeleteButton
                      onClick={() => {
                        if (product.Item) {
                          deleteTicketFromCart(product.Item.pk, product.Item.sk)
                        }
                      }}
                      color={companyProfile?.primary_main}
                    />
                  </div>
                </div>
                <div
                  id="dropdown"
                  className={classNames(
                    'z-40 mt-2 w-44 divide-y rounded-lg bg-white shadow',
                    openDropdown[index] ? 'block' : 'hidden'
                  )}
                  style={{
                    borderRightColor: companyProfile?.primary_main,
                    backgroundColor: companyProfile?.primary_main,
                    borderRightWidth: 2,
                  }}
                >
                  <ul
                    className="py-2 text-sm text-gray-700"
                    aria-labelledby="dropdownDefaultButton"
                    style={{
                      color: companyProfile?.primary_main,
                    }}
                  >
                    {product.Item.sizes?.map((size, sizeIndex) => (
                      <li key={`${sizeIndex}-${size.name}`}>
                        <div
                          className={classNames(
                            companyProfile?.primary_inverted
                              ? 'text-gray-600'
                              : 'text-white',
                            'block cursor-pointer px-4 py-2'
                          )}
                          onClick={() => {
                            if (product.Item) {
                              updateSize(product.Item, size, sizeIndex)
                            }
                          }}
                        >
                          {capitalize(size.name ?? '')}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            </React.Fragment>
          )
        }
      })}
    </>
  )
}

interface PublicPoolListProps {
  items: Cart['items']
  deleteTicketFromCart: (pk: string, sk: string) => void
  companyProfile: CompanyProfile | null
  t: TFunction
  openDropdown: { [key: number]: boolean }
  setOpenDropdown: React.Dispatch<
    React.SetStateAction<{ [key: number]: boolean }>
  >
  updatePrice: (
    pool: PublicPool,
    price: Price,
    index: number,
    productIndex: number
  ) => void
}

// Item List Component
const PublicPoolList: React.FC<PublicPoolListProps> = ({
  items,
  deleteTicketFromCart,
  companyProfile,
  t,
  openDropdown,
  setOpenDropdown,
  updatePrice,
}) => {
  const groupedItems = items.reduce((acc, product) => {
    if (product.PublicPool) {
      const key = `${product.PublicPool.pk}-${product.PublicPool.sk}-${product.PublicPool.selected_price?.name}`
      if (!acc[key]) {
        acc[key] = { ...product, quantity: 1 }
      } else {
        acc[key].quantity += 1
      }
    }
    return acc
  }, {} as { [key: string]: Product & { quantity: number } })

  return (
    <>
      {Object.values(groupedItems).map((product, index) => {
        if (product.PublicPool !== undefined) {
          const isFirstItem = index === 0
          return (
            <React.Fragment
              key={`${index}-${product.PublicPool.pk}-${product.PublicPool.sk}`}
            >
              {isFirstItem && (
                <h2
                  id="cart-heading"
                  className="-mb-2 mt-5 text-lg font-semibold text-gray-600"
                  style={{
                    fontFamily: companyProfile?.font?.name,
                  }}
                >
                  {t('pools')}
                </h2>
              )}
              <li className="mt-4 flex w-full flex-col pr-4">
                <div className="flex flex-row rounded-l">
                  <div className="w-[90%] min-w-[320px]">
                    <div className="flex flex-row rounded-l drop-shadow-md">
                      <div className="w-1/5 rounded-l">
                        <div
                          id="bild"
                          style={{
                            borderRightWidth: 2,
                            borderRightColor: companyProfile?.primary_main,
                          }}
                          className="flex h-full w-full items-center rounded-l bg-white"
                        >
                          <ItemPicture
                            imageLink={
                              product.PublicPool.image_link ?? undefined
                            }
                          />
                        </div>
                      </div>
                      <div className="flex w-4/5 flex-col">
                        <div
                          style={{
                            borderBottomColor: companyProfile?.primary_inverted
                              ? '#000000'
                              : companyProfile?.primary_main,
                            color: companyProfile?.primary_inverted
                              ? '#000000'
                              : companyProfile?.primary_main,
                            borderBottomWidth: 2,
                          }}
                          className="flex h-1/2 items-center rounded-tr bg-white py-4 pl-3 text-base font-medium leading-tight"
                        >
                          <p className="">
                            {product.PublicPool.name} (x{product.quantity})
                          </p>
                        </div>
                        <div className="flex h-1/2 w-full flex-row">
                          <div
                            className="flex w-3/5 cursor-pointer flex-row items-center justify-between rounded-bl bg-white p-1 px-2 text-white"
                            onClick={() =>
                              setOpenDropdown((prev) => ({
                                ...prev,
                                [index]: !prev[index],
                              }))
                            }
                          >
                            <p className="ml-1 text-sm text-black">
                              {capitalize(
                                product.PublicPool.selected_price?.name ?? ''
                              )}
                              <ChevronDownIcon className="inline-block h-4 w-4 text-black" />
                            </p>
                          </div>
                          <div
                            style={{
                              borderLeftColor: companyProfile?.primary_main,
                              borderLeftWidth: 2,
                            }}
                            className="flex w-2/5 flex-row items-center justify-center rounded-br bg-white"
                          >
                            <PriceDisplay
                              value={
                                (product.PublicPool.selected_price?.value ??
                                  0) * product.quantity
                              }
                              currency={'SEK'}
                              color={
                                companyProfile?.primary_inverted
                                  ? '#4B5563'
                                  : companyProfile?.primary_main
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 self-center">
                    <DeleteButton
                      onClick={() => {
                        if (product.PublicPool) {
                          deleteTicketFromCart(
                            product.PublicPool.pk,
                            product.PublicPool.sk
                          )
                        }
                      }}
                      color={companyProfile?.primary_main}
                    />
                  </div>
                </div>
                <div
                  id="dropdown"
                  className={classNames(
                    'z-40 mt-2 w-44 divide-y rounded-lg bg-white shadow',
                    openDropdown[index] ? 'block' : 'hidden'
                  )}
                  style={{
                    backgroundColor: companyProfile?.primary_main,
                  }}
                >
                  <ul
                    className="py-2 text-sm text-gray-700"
                    aria-labelledby="dropdownDefaultButton"
                  >
                    {product.PublicPool.prices?.map((price, priceIndex) => (
                      <li key={`${priceIndex}-${price.name}`}>
                        <div
                          className={classNames(
                            companyProfile?.primary_inverted
                              ? 'text-gray-600'
                              : 'text-white',
                            'block cursor-pointer px-4 py-2'
                          )}
                          onClick={() => {
                            if (product.PublicPool) {
                              updatePrice(
                                product.PublicPool,
                                price,
                                priceIndex,
                                index
                              )
                            }
                          }}
                        >
                          {capitalize(price.name ?? '')} - {price.value} SEK
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            </React.Fragment>
          )
        }
      })}
    </>
  )
}

interface KioskListProps {
  items: Cart['items']
  deleteTicketFromCart: (pk: string, sk: string) => void
  companyProfile: CompanyProfile | null
  t: TFunction
}

// Kiosk List Component
const KioskList: React.FC<KioskListProps> = ({
  items,
  deleteTicketFromCart,
  companyProfile,
  t,
}) => {
  const groupedItems = items.reduce((acc, product) => {
    if (product.Kiosk) {
      const key = `${product.Kiosk.pk}-${product.Kiosk.sk}`
      if (!acc[key]) {
        acc[key] = { ...product, quantity: 1 }
      } else {
        acc[key].quantity += 1
      }
    }
    return acc
  }, {} as { [key: string]: Product & { quantity: number } })

  return (
    <>
      {Object.values(groupedItems).map((product, index) => {
        if (product.Kiosk !== undefined) {
          const isFirstItem = index === 0
          return (
            <React.Fragment
              key={`${index}-${product.Kiosk.pk}-${product.Kiosk.sk}`}
            >
              {isFirstItem && (
                <h2
                  id="cart-heading"
                  className="-mb-2 mt-5 text-lg font-semibold text-gray-600"
                  style={{
                    fontFamily: companyProfile?.font?.name,
                  }}
                >
                  {t('kiosk')}
                </h2>
              )}
              <li className="mt-4 flex w-full flex-col pr-4">
                <div className="flex flex-row rounded-l">
                  <div className="w-[90%] min-w-[320px]">
                    <div className="flex flex-row rounded-l drop-shadow-md">
                      <div className="w-1/5 rounded-l">
                        <div
                          id="bild"
                          style={{
                            borderRightWidth: 2,
                            borderRightColor: companyProfile?.primary_main,
                          }}
                          className="flex h-full w-full items-center rounded-l bg-white"
                        >
                          <ItemPicture
                            imageLink={product.Kiosk.image_link ?? undefined}
                          />
                        </div>
                      </div>
                      <div className="flex w-4/5 flex-col">
                        <div
                          style={{
                            borderBottomColor: companyProfile?.primary_inverted
                              ? '#000000'
                              : companyProfile?.primary_main,
                            color: companyProfile?.primary_inverted
                              ? '#000000'
                              : companyProfile?.primary_main,
                            borderBottomWidth: 2,
                          }}
                          className="flex h-1/2 items-center rounded-tr bg-white py-4 pl-3 text-base font-medium leading-tight"
                        >
                          <p className="">
                            {product.Kiosk.name} (x{product.quantity})
                          </p>
                        </div>
                        <div className="flex h-1/2 w-full flex-row">
                          <div className="flex w-3/5 items-center bg-white py-1 pl-3">
                            <p className="text-sm text-gray-600">
                              {truncateForCart(product.Kiosk.description)}
                            </p>
                          </div>
                          <div
                            style={{
                              borderLeftColor: companyProfile?.primary_main,
                              borderLeftWidth: 2,
                            }}
                            className="flex w-2/5 flex-row items-center justify-center rounded-br bg-white"
                          >
                            <PriceDisplay
                              value={
                                (product.Kiosk.selected_price?.value ?? 0) *
                                product.quantity
                              }
                              currency={product.Kiosk.currency}
                              color={
                                companyProfile?.primary_inverted
                                  ? '#4B5563'
                                  : companyProfile?.primary_main
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 self-center">
                    <DeleteButton
                      onClick={() =>
                        deleteTicketFromCart(
                          product.Kiosk?.pk ?? '',
                          product.Kiosk?.sk ?? ''
                        )
                      }
                      color={companyProfile?.primary_main}
                    />
                  </div>
                </div>
              </li>
            </React.Fragment>
          )
        }
      })}
    </>
  )
}

interface RestaurantListProps {
  items: Cart['items']
  deleteTicketFromCart: (pk: string, sk: string) => void
  companyProfile: CompanyProfile | null
  t: TFunction
}

// Restaurant List Component
const RestaurantList: React.FC<RestaurantListProps> = ({
  items,
  deleteTicketFromCart,
  companyProfile,
  t,
}) => (
  <>
    {items?.map((product, index) => {
      if (product.RestaurantBooking !== undefined) {
        return (
          <React.Fragment
            key={`${index}-${product.RestaurantBooking.pk}-${product.RestaurantBooking.sk}`}
          >
            <h2
              id="cart-heading"
              className="-mb-2 mt-5 text-lg font-semibold text-gray-600"
            >
              {product.RestaurantBooking.name} -{' '}
              {product.RestaurantBooking.slot.seats} {t('seats')} (
              {product.RestaurantBooking.slot.time})
            </h2>
          </React.Fragment>
        )
      }
      if (product.Restaurant !== undefined) {
        const isFirstItem =
          items.findIndex(
            (restaurant) => restaurant.Restaurant !== undefined
          ) === index
        return (
          <React.Fragment
            key={`${index}-${product.Restaurant.pk}-${product.Restaurant.sk}`}
          >
            {isFirstItem && (
              <h2
                id="cart-heading"
                className="-mb-2 mt-5 text-lg font-semibold text-gray-600"
                style={{
                  fontFamily: companyProfile?.font?.name,
                }}
              >
                {t('restaurant')}
              </h2>
            )}
            <li className="mt-4 flex w-full flex-col  pr-4">
              <div className="flex flex-row">
                <div className="w-[90%] min-w-[320px]">
                  <div className="flex w-full flex-row">
                    <div
                      style={{
                        borderRightColor: companyProfile?.primary_main,
                        color: companyProfile?.primary_main,
                        borderRightWidth: 2,
                      }}
                      className="flex w-3/4 items-center rounded-l bg-white p-2 text-base font-extrabold "
                    >
                      {product.Restaurant.name}
                    </div>
                    <div
                      style={{
                        color: companyProfile?.primary_main,
                      }}
                      className="flex w-1/4 flex-row items-center justify-center rounded-r bg-white p-2"
                    >
                      <PriceDisplay
                        value={product.Restaurant.selected_price?.value}
                        currency={product.Restaurant.currency}
                        color={
                          companyProfile?.primary_inverted
                            ? '#4B5563'
                            : companyProfile?.primary_main
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="ml-4 self-center">
                  <DeleteButton
                    onClick={() =>
                      deleteTicketFromCart(
                        product.Restaurant?.pk ?? '',
                        product.Restaurant?.sk ?? ''
                      )
                    }
                    color={companyProfile?.primary_main}
                  />
                </div>
              </div>
            </li>
          </React.Fragment>
        )
      }
    })}
  </>
)

interface MembershipListProps {
  items: Cart['items']
  deleteTicketFromCart: (pk: string, sk: string) => void
  currency: Currency
  companyProfile: CompanyProfile | null
  t: TFunction
}

// Membership List Component
const MembershipList: React.FC<MembershipListProps> = ({
  items,
  deleteTicketFromCart,
  currency,
  companyProfile,
  t,
}) => (
  <>
    {items?.map((product, index) => {
      if (product.Membership !== undefined) {
        const isFirstItem =
          items.findIndex((item) => item.Membership !== undefined) === index
        return (
          <React.Fragment
            key={`${index}-${product.Membership.pk}-${product.Membership.sk}`}
          >
            {isFirstItem && (
              <h2
                id="cart-heading"
                className="-mb-2 mt-5 text-lg font-semibold text-gray-600"
                style={{
                  fontFamily: companyProfile?.font?.name,
                }}
              >
                {t('memberships')}
              </h2>
            )}
            <li className="mt-4 flex w-full flex-col pr-4">
              <div className="flex flex-row rounded-l">
                <div className="w-[90%] min-w-[320px]">
                  <div className="flex flex-row rounded-l drop-shadow-md">
                    <div className="w-1/5 rounded-l">
                      <div
                        id="bild"
                        style={{
                          borderRightWidth: 2,
                          borderRightColor: companyProfile?.primary_main,
                        }}
                        className="flex h-full w-full items-center rounded-l bg-white"
                      >
                        <ItemPicture
                          imageLink={product.Membership.image_link ?? undefined}
                        />
                      </div>
                    </div>
                    <div className="flex w-4/5 flex-col">
                      <div
                        style={{
                          borderBottomColor: companyProfile?.primary_inverted
                            ? '#000000'
                            : companyProfile?.primary_main,
                          color: companyProfile?.primary_inverted
                            ? '#000000'
                            : companyProfile?.primary_main,
                          borderBottomWidth: 2,
                        }}
                        className="flex h-1/2 items-center rounded-tr bg-white py-4 pl-3 text-base font-medium leading-tight"
                      >
                        <p className="">{product.Membership.name}</p>
                      </div>
                      <div className="flex h-1/2 w-full flex-row">
                        <div className="flex w-3/5 cursor-pointer flex-row items-center justify-between rounded-bl bg-white p-1 px-2 text-white">
                          <p className="ml-1 text-sm text-black">
                            {capitalize(
                              product.Membership.selected_price?.name ?? ''
                            )}
                          </p>
                        </div>
                        <div
                          style={{
                            borderLeftColor: companyProfile?.primary_main,
                            borderLeftWidth: 2,
                          }}
                          className="flex w-2/5 flex-row items-center justify-center rounded-br bg-white"
                        >
                          <PriceDisplay
                            value={product.Membership.selected_price?.value}
                            currency={currency.toString()}
                            color={
                              companyProfile?.primary_inverted
                                ? '#4B5563'
                                : companyProfile?.primary_main
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="ml-4 self-center">
                  <DeleteButton
                    onClick={() =>
                      deleteTicketFromCart(
                        product.Membership?.pk ?? '',
                        product.Membership?.sk ?? ''
                      )
                    }
                    color={companyProfile?.primary_main}
                  />
                </div>
              </div>
            </li>
          </React.Fragment>
        )
      }
    })}
  </>
)

// Main VenueCart Component
const VenueCart: React.FC<VenueCartProps> = ({
  eventId,
  seasonTicketId,
  organisationId,
  currentStep,
  setCurrentStep,
  cart,
  refetchCart,
  refetchTickets,
  setShowSuccessMessage,
  setShowFailureMessage,
  companyProfile,
  shouldHaveItems,
  shouldHaveKiosk,
  shouldHaveRestaurant,
  ticketsInCart,
  isRegister,
  showOrder,
  setShowOrder,
  showCampaignCode,
  setShowCampaignCode,
  hideProceedButton,
}) => {
  const { state } = useContext(AuthContext)
  const { t } = useTranslation('common')
  const [openDropdown, setOpenDropdown] = useState<{ [key: number]: boolean }>(
    {}
  )
  const numberOfSteps =
    2 +
    (shouldHaveItems ? 1 : 0) +
    (shouldHaveKiosk ? 1 : 0) +
    (shouldHaveRestaurant ? 1 : 0)

  const [cartTimeToDelete, setCartTimeToDelete] = useState<number | null>(null)
  const [timeLeft, setTimeLeft] = useState<number | null>(null)

  const [campaignCode, setCampaignCode] = useState('')
  const [campaignCodeError, setCampaignCodeError] = useState('')
  const [campaignCodeSuccess, setCampaignCodeSuccess] = useState('')

  const [isFixed, setIsFixed] = useState(true)
  const orderSummaryRef = useRef<HTMLDivElement | null>(null)
  const proceedButtonRef = useRef<HTMLDivElement | null>(null)
  const lastScrollY = useRef(0)

  // Effect for cart time to delete
  useEffect(() => {
    if (cart?.time_to_delete !== null && cart?.time_to_delete !== undefined) {
      const currentTime = Math.floor(new Date().getTime() / 1000)
      const diff = cart?.time_to_delete - currentTime
      setCartTimeToDelete(diff > 0 ? diff : null)
      setTimeLeft(diff > 0 ? diff : null)
    }
  }, [cart])

  // Effect for countdown timer
  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeLeft((timeLeft) =>
        timeLeft !== null
          ? timeLeft > 0
            ? timeLeft - 1
            : timeLeft
          : cartTimeToDelete
      )
    }, 1000)

    return () => clearInterval(intervalId)
  }, [cartTimeToDelete])

  // Effect for scroll handling
  useEffect(() => {
    const handleScroll = () => {
      if (!orderSummaryRef.current || !proceedButtonRef.current) return

      const scrollingDown = window.scrollY > lastScrollY.current
      lastScrollY.current = window.scrollY

      const summaryBottom =
        orderSummaryRef.current.getBoundingClientRect().bottom
      const buttonHeight = proceedButtonRef.current.offsetHeight
      const windowHeight = window.innerHeight

      if (scrollingDown) {
        const isButtonFixed = summaryBottom > windowHeight
        setIsFixed(isButtonFixed)
      } else {
        const isButtonFixed = summaryBottom + buttonHeight > windowHeight
        setIsFixed(isButtonFixed)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Function to delete ticket from cart
  const deleteTicketFromCart = async (pk: string, sk: string) => {
    try {
      const url = eventId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventId}/cart`
        : seasonTicketId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${seasonTicketId}/cart`
        : organisationId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${organisationId}/cart`
        : null

      if (!url) {
        throw new Error(
          t('no-valid-id-for-delete') ??
            'No valid ID provided for delete operation'
        )
      }

      const response = await fetch(url, {
        method: 'DELETE',
        body: JSON.stringify({ pk, sk }),
        headers: {
          'Content-Type': 'application/json',
          'Cart-Token': state.cartToken,
          Authorization: `Bearer ${await clientGetIdToken()}`,
        },
      })
      await response.json()

      setShowSuccessMessage(t('product-deleted') ?? 'Product deleted')
    } catch (err) {
      captureException(err)
      setShowFailureMessage(
        t('product-deleted-failed') ?? 'Product could not be deleted'
      )
    } finally {
      refetchCart()
      refetchTickets()
    }
  }

  // Function to delete campaign from cart
  const deleteCampaignFromCart = async (code: string) => {
    try {
      const url = eventId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventId}/cart/campaign_code`
        : seasonTicketId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${seasonTicketId}/cart/campaign_code`
        : organisationId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${organisationId}/cart/campaign_code`
        : null

      if (!url) {
        throw new Error(
          t('no-valid-id-for-delete-campaign') ??
            'No valid ID provided for delete campaign operation'
        )
      }

      const response = await fetch(url, {
        method: 'DELETE',
        body: JSON.stringify({ code }),
        headers: {
          'Content-Type': 'application/json',
          'Cart-Token': state.cartToken,
          Authorization: `Bearer ${await clientGetIdToken()}`,
        },
      })
      await response.json()

      setShowSuccessMessage(t('campaign-deleted') ?? 'Campaign deleted')
    } catch (err) {
      captureException(err)
      setShowFailureMessage(
        t('campaign-deleted-failed') ?? 'Campaign could not be deleted'
      )
    } finally {
      refetchCart()
      refetchTickets()
    }
  }

  // Function to update price
  const updatePrice = async (ticket: Ticket, price: Price, index: number) => {
    try {
      const url = eventId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventId}/cart`
        : seasonTicketId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${seasonTicketId}/cart`
        : organisationId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${organisationId}/cart`
        : null

      if (!url) {
        throw new Error(
          t('no-valid-id-for-update-price') ??
            'No valid ID provided for update price operation'
        )
      }

      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({ pk: ticket.pk, sk: ticket.sk, price }),
        headers: {
          'Content-Type': 'application/json',
          'Cart-Token': state.cartToken,
          Authorization: `Bearer ${await clientGetIdToken()}`,
        },
      })
      await response.json()
      setShowSuccessMessage(t('price-updated') ?? 'Price updated')
    } catch (err) {
      captureException(err)
      setShowFailureMessage(
        t('price-update-failed') ?? 'Price could not be updated'
      )
    } finally {
      refetchCart()
      setOpenDropdown((prev) => ({
        ...prev,
        [index]: !prev[index],
      }))
    }
  }

  // Function to update price
  const updatePoolPrice = async (
    pool: PublicPool,
    price: Price,
    index: number,
    productIndex: number
  ) => {
    try {
      const url = eventId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventId}/cart`
        : seasonTicketId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${seasonTicketId}/cart`
        : organisationId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${organisationId}/cart`
        : null

      if (!url) {
        throw new Error(
          t('no-valid-id-for-update-price') ??
            'No valid ID provided for update price operation'
        )
      }

      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({
          pk: pool.pk,
          sk: pool.sk,
          price,
          index: productIndex,
        }),
        headers: {
          'Content-Type': 'application/json',
          'Cart-Token': state.cartToken,
          Authorization: `Bearer ${await clientGetIdToken()}`,
        },
      })
      await response.json()
      setShowSuccessMessage(t('price-updated') ?? 'Price updated')
    } catch (err) {
      captureException(err)
      setShowFailureMessage(
        t('price-update-failed') ?? 'Price could not be updated'
      )
    } finally {
      refetchCart()
      setOpenDropdown((prev) => ({
        ...prev,
        [index]: !prev[index],
      }))
    }
  }

  // Function to update size
  const updateSize = async (item: Item, size: Size, index: number) => {
    try {
      const url = eventId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventId}/cart`
        : seasonTicketId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${seasonTicketId}/cart`
        : organisationId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${organisationId}/cart`
        : null

      if (!url) {
        throw new Error(
          t('no-valid-id-for-update-size') ??
            'No valid ID provided for update size operation'
        )
      }

      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({ pk: item.pk, sk: item.sk, size }),
        headers: {
          'Content-Type': 'application/json',
          'Cart-Token': state.cartToken,
          Authorization: `Bearer ${await clientGetIdToken()}`,
        },
      })
      await response.json()
      setShowSuccessMessage(t('size-updated') ?? 'Size updated')
    } catch (err) {
      captureException(err)
      setShowFailureMessage(
        t('size-update-failed') ?? 'Size could not be updated'
      )
    } finally {
      refetchCart()
      setOpenDropdown((prev) => ({
        ...prev,
        [index]: !prev[index],
      }))
    }
  }

  // Function to add campaign code
  const addCampaignCode = async () => {
    setCampaignCodeError('')
    setCampaignCodeSuccess('')
    try {
      const url = eventId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventId}/cart/campaign_code`
        : seasonTicketId
        ? `${process.env.NEXT_PUBLIC_API_URL}/checkout/${seasonTicketId}/campaign_code`
        : null

      if (!url) {
        throw new Error(
          t('no-valid-id-for-add-campaign-code') ??
            'No valid ID provided for add campaign code operation'
        )
      }

      const response = await fetch(url, {
        method: 'PUT',
        body: JSON.stringify({ code: campaignCode }),
        headers: {
          'Content-Type': 'application/json',
          'Cart-Token': state.cartToken,
          Authorization: `Bearer ${await clientGetIdToken()}`,
        },
      })
      const res: StandardAPIRequest<Cart> = await response.json()
      if (res.data !== null) {
        setCampaignCodeSuccess(
          t('campaign-code-added') ?? 'Campaign code added'
        )
        setCampaignCode('')
      } else {
        setCampaignCodeError(
          t('campaign-code-add-failed') ?? 'Campaign code could not be added'
        )
      }
    } catch (err) {
      captureException(err)
      setCampaignCodeError(
        t('campaign-code-add-failed') ?? 'Campaign code could not be added'
      )
    } finally {
      refetchCart()
    }
  }

  return (
    <>
      <Transition.Root show={showOrder} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => setShowOrder(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 hidden bg-gray-500 bg-opacity-75 transition-opacity md:block" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-stretch justify-center text-center md:items-center md:px-2 lg:px-4">
              <span
                className="hidden md:inline-block md:h-screen md:align-middle"
                aria-hidden="true"
              >
                &#8203;
              </span>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
                enterTo="opacity-100 translate-y-0 md:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 md:scale-100"
                leaveTo="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
              >
                <Dialog.Panel className="flex w-full text-left text-base transition md:my-8 md:max-w-xl md:px-4 lg:max-w-2xl">
                  <div className="relative flex w-full flex-col items-center overflow-hidden rounded bg-white shadow-2xl">
                    <div className="flex h-full w-full flex-col justify-between">
                      <div className="w-full overflow-y-scroll pl-8 pr-4 pt-8 lg:px-0">
                        {timeLeft !== null &&
                          timeLeft > 0 &&
                          ticketsInCart.length > 0 && (
                            <div>
                              <p className="mb-4 text-xs font-bold text-gray-900">
                                {`${t('time-left')}: ${formatTimeLeft(
                                  timeLeft,
                                  t
                                )}`}
                              </p>
                            </div>
                          )}
                        <div className="flex w-full flex-row justify-between">
                          <h1 className="text-left text-2xl font-semibold tracking-tight text-gray-700">
                            {t('your-order')}
                          </h1>
                          <button
                            type="button"
                            className="text-gray-400 hover:text-gray-500"
                            onClick={() => setShowOrder(false)}
                          >
                            <span className="sr-only">{t('close')}</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                        {(cart?.items.length ?? 0) > 0 && (
                          <React.Fragment>
                            {showCampaignCode ? (
                              <div>
                                <label
                                  htmlFor="campaign_code"
                                  className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                  {t('campaign-code')}
                                </label>
                                <div className="mt-2 flex rounded-md shadow-sm">
                                  <div className="relative flex flex-grow items-stretch focus-within:z-10">
                                    <input
                                      id="campaign_code"
                                      name="campaign_code"
                                      type="text"
                                      placeholder="********"
                                      className="block w-full rounded-none rounded-l-md border-0 py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                                      onChange={(e) =>
                                        setCampaignCode(e.target.value)
                                      }
                                      value={campaignCode}
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                                    onClick={addCampaignCode}
                                  >
                                    <PlusIcon
                                      aria-hidden="true"
                                      className="-ml-0.5 h-5 w-5 text-gray-400"
                                    />
                                    {t('add')}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div
                                className="flex cursor-pointer flex-row"
                                onClick={() => setShowCampaignCode(true)}
                              >
                                <p className="block text-sm font-medium leading-6 text-gray-900">
                                  {t('have-campaign-code')}
                                </p>
                                <p className="ml-1 block text-sm font-semibold leading-6 text-blue-500">
                                  {t('click-here')}
                                </p>
                              </div>
                            )}
                          </React.Fragment>
                        )}
                        {campaignCodeError && (
                          <p className="text-sm text-red-500">
                            {campaignCodeError}
                          </p>
                        )}
                        {campaignCodeSuccess && (
                          <p className="text-sm text-green-500">
                            {campaignCodeSuccess}
                          </p>
                        )}
                        <CampaignList
                          campaigns={cart?.campaigns}
                          deleteCampaignFromCart={deleteCampaignFromCart}
                          companyProfile={companyProfile}
                          t={t}
                        />
                        <div className="mt-5 flex flex-col pb-10">
                          <section aria-labelledby="cart-heading">
                            {cart?.items?.some(
                              (product) => product.Ticket !== undefined
                            ) && (
                              <h2
                                id="cart-heading"
                                className="-mb-3 text-lg font-semibold text-gray-600"
                                style={{
                                  fontFamily: companyProfile?.font?.name,
                                }}
                              >
                                {t('tickets')}
                              </h2>
                            )}
                            <TicketList
                              tickets={cart?.items ?? []}
                              deleteTicketFromCart={deleteTicketFromCart}
                              updatePrice={updatePrice}
                              companyProfile={companyProfile}
                              openDropdown={openDropdown}
                              setOpenDropdown={setOpenDropdown}
                              t={t}
                            />
                            <ItemList
                              items={cart?.items ?? []}
                              deleteTicketFromCart={deleteTicketFromCart}
                              updateSize={updateSize}
                              companyProfile={companyProfile}
                              openDropdown={openDropdown}
                              setOpenDropdown={setOpenDropdown}
                              t={t}
                            />
                            <PublicPoolList
                              items={cart?.items ?? []}
                              deleteTicketFromCart={deleteTicketFromCart}
                              companyProfile={companyProfile}
                              openDropdown={openDropdown}
                              setOpenDropdown={setOpenDropdown}
                              updatePrice={updatePoolPrice}
                              t={t}
                            />
                            <KioskList
                              items={cart?.items ?? []}
                              deleteTicketFromCart={deleteTicketFromCart}
                              companyProfile={companyProfile}
                              t={t}
                            />
                            <RestaurantList
                              items={cart?.items ?? []}
                              deleteTicketFromCart={deleteTicketFromCart}
                              companyProfile={companyProfile}
                              t={t}
                            />
                            <MembershipList
                              items={cart?.items ?? []}
                              deleteTicketFromCart={deleteTicketFromCart}
                              companyProfile={companyProfile}
                              currency={cart?.currency ?? Currency.SEK}
                              t={t}
                            />
                          </section>
                          <section
                            aria-labelledby="summary-heading"
                            className=""
                          >
                            <h2 id="summary-heading" className="sr-only">
                              {t('order-summary')}
                            </h2>
                          </section>
                        </div>
                      </div>
                      <div className={classNames('px-6 py-4')}>
                        <button
                          type="submit"
                          style={{
                            backgroundColor: companyProfile?.secondary_main,
                          }}
                          className={classNames(
                            companyProfile?.secondary_inverted
                              ? 'text-gray-900'
                              : 'text-white',
                            'block w-full rounded-md px-4 py-3 text-center text-base font-bold shadow-sm hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-50'
                          )}
                          onClick={() => setShowOrder(false)}
                        >
                          {t('close')}
                        </button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      {!hideProceedButton && (
        <React.Fragment>
          {!showOrder && (
            <div className="fixed bottom-0 z-30 block w-screen rounded-t-xl sm:hidden">
              <div className={classNames('rounded-t-xl bg-gray-200 px-6 py-4')}>
                <div className="flex flex-row items-center justify-between">
                  <button
                    type="submit"
                    style={{
                      backgroundColor: companyProfile?.secondary_main,
                    }}
                    className={classNames(
                      companyProfile?.secondary_inverted
                        ? 'text-gray-900'
                        : 'text-white',
                      'block w-full rounded-md px-4 py-3 text-center text-base font-bold shadow-sm hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-50'
                    )}
                    onClick={() => {
                      if (
                        currentStep + 1 === numberOfSteps &&
                        (cart?.items?.length === 0 || cart === undefined)
                      ) {
                        setShowFailureMessage(
                          t('cannot-proceed-empty-cart') ??
                            'Cannot proceed with empty cart'
                        )
                      } else {
                        setCurrentStep((localCurrentStep) =>
                          localCurrentStep < numberOfSteps
                            ? localCurrentStep + 1
                            : localCurrentStep
                        )
                        window.scrollTo(0, 0)
                      }
                    }}
                  >
                    {t('proceed')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </React.Fragment>
      )}
      <div className="hidden h-full grow flex-col sm:flex">
        <div
          ref={orderSummaryRef}
          className="flex w-screen grow justify-between overflow-y-scroll bg-gray-100 pl-4 sm:w-full"
        >
          <div id="div1" className="w-full px-4 pt-8 lg:px-0">
            {timeLeft !== null && timeLeft > 0 && ticketsInCart.length > 0 && (
              <div>
                <p className="mb-4 text-xs font-bold text-gray-900">
                  {`${t('time-left')}: ${formatTimeLeft(timeLeft, t)}`}
                </p>
              </div>
            )}
            <h1
              className="text-left text-2xl font-semibold tracking-tight text-gray-600"
              style={{
                fontFamily: companyProfile?.font?.name,
              }}
            >
              {t('your-order')}
            </h1>
            {(cart?.items.length ?? 0) > 0 && (
              <React.Fragment>
                {showCampaignCode ? (
                  <div>
                    <label
                      htmlFor="campaign_code"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      {t('campaign-code')}
                    </label>
                    <div className="mt-2 flex rounded-md shadow-sm">
                      <div className="relative flex flex-grow items-stretch focus-within:z-10">
                        <input
                          id="campaign_code"
                          name="campaign_code"
                          type="text"
                          placeholder="********"
                          className="block w-full rounded-none rounded-l-md border-0 py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                          onChange={(e) => setCampaignCode(e.target.value)}
                          value={campaignCode}
                        />
                      </div>
                      <button
                        type="button"
                        className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                        onClick={addCampaignCode}
                      >
                        <PlusIcon
                          aria-hidden="true"
                          className="-ml-0.5 h-5 w-5 text-gray-400"
                        />
                        {t('add')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex cursor-pointer flex-row"
                    onClick={() => setShowCampaignCode(true)}
                  >
                    <p className="block text-sm font-medium leading-6 text-gray-900">
                      {t('have-campaign-code')}
                    </p>
                    <p className="ml-1 block text-sm font-semibold leading-6 text-blue-500">
                      {t('click-here')}
                    </p>
                  </div>
                )}
              </React.Fragment>
            )}
            {campaignCodeError && (
              <p className="text-sm text-red-500">{campaignCodeError}</p>
            )}
            {campaignCodeSuccess && (
              <p className="text-sm text-green-500">{campaignCodeSuccess}</p>
            )}
            <CampaignList
              campaigns={cart?.campaigns}
              deleteCampaignFromCart={deleteCampaignFromCart}
              companyProfile={companyProfile}
              t={t}
            />
            <div className="mt-5 flex flex-col pb-10">
              <section aria-labelledby="cart-heading">
                {cart?.items?.some(
                  (product) => product.Ticket !== undefined
                ) && (
                  <h2
                    id="cart-heading"
                    className="-mb-3 text-lg font-semibold text-gray-600"
                    style={{
                      fontFamily: companyProfile?.font?.name,
                    }}
                  >
                    {t('tickets')}
                  </h2>
                )}
                <TicketList
                  tickets={cart?.items ?? []}
                  deleteTicketFromCart={deleteTicketFromCart}
                  updatePrice={updatePrice}
                  companyProfile={companyProfile}
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  t={t}
                />
                <PublicPoolList
                  items={cart?.items ?? []}
                  deleteTicketFromCart={deleteTicketFromCart}
                  companyProfile={companyProfile}
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  updatePrice={updatePoolPrice}
                  t={t}
                />
                <ItemList
                  items={cart?.items ?? []}
                  deleteTicketFromCart={deleteTicketFromCart}
                  updateSize={updateSize}
                  companyProfile={companyProfile}
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  t={t}
                />
                <KioskList
                  items={cart?.items ?? []}
                  deleteTicketFromCart={deleteTicketFromCart}
                  companyProfile={companyProfile}
                  t={t}
                />
                <RestaurantList
                  items={cart?.items ?? []}
                  deleteTicketFromCart={deleteTicketFromCart}
                  companyProfile={companyProfile}
                  t={t}
                />
                <MembershipList
                  items={cart?.items ?? []}
                  deleteTicketFromCart={deleteTicketFromCart}
                  companyProfile={companyProfile}
                  currency={cart?.currency ?? Currency.SEK}
                  t={t}
                />
              </section>
              <section aria-labelledby="summary-heading" className="">
                <h2 id="summary-heading" className="sr-only">
                  {t('order-summary')}
                </h2>
              </section>
            </div>
          </div>
        </div>

        <div
          ref={proceedButtonRef}
          className={classNames(
            isFixed && !isRegister
              ? 'fixed bottom-0 right-0 mt-auto w-1/4'
              : '',
            'bg-gray-200 pb-12 pt-3'
          )}
        >
          <div className="mx-6">
            <div>
              <dl className="gap-y-4">
                <div className="flex items-center justify-between">
                  <dt className="text-base font-medium text-gray-600">
                    {t('total-amount')}
                  </dt>
                  {(cart?.original_price ?? 0) > (cart?.total_price ?? 0) ? (
                    <dd className="ml-4 text-base font-normal text-gray-600">
                      <span className="mr-1 line-through">
                        {((cart?.original_price ?? 0) / 100).toFixed(2)}
                      </span>
                      {((cart?.total_price ?? 0) / 100).toFixed(2)}{' '}
                      {t('kr-incl-vat', {
                        currency: cart?.currency.toString(),
                      })}
                    </dd>
                  ) : (
                    <dd className="ml-4 text-base font-normal text-gray-600">
                      {((cart?.total_price ?? 0) / 100).toFixed(2)}{' '}
                      {t('kr-incl-vat', {
                        currency: cart?.currency.toString(),
                      })}
                    </dd>
                  )}
                </div>
              </dl>
            </div>

            <div className="mt-10">
              <button
                type="submit"
                style={{
                  backgroundColor: companyProfile?.secondary_main,
                }}
                className={classNames(
                  companyProfile?.secondary_inverted
                    ? 'text-gray-900'
                    : 'text-white',
                  'block w-full rounded-md px-4 py-3 text-center text-base font-bold shadow-sm hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-50'
                )}
                onClick={() => {
                  if (
                    currentStep + 1 === numberOfSteps &&
                    (cart?.items?.length === 0 || cart === undefined)
                  ) {
                    setShowFailureMessage(
                      t('cannot-proceed-empty-cart') ??
                        'Cannot proceed with empty cart'
                    )
                  } else {
                    setCurrentStep((localCurrentStep) =>
                      localCurrentStep < numberOfSteps
                        ? localCurrentStep + 1
                        : localCurrentStep
                    )
                    window.scrollTo(0, 0)
                  }
                }}
              >
                {t('proceed')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export { VenueCart }
